import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarPlus, Upload, Mail, Trash2, MapPin, Lock, CreditCard } from "lucide-react";

// Standalone "Create event" form used after registration. It only asks for event-specific
// details — the creator's Company Logo and Organizer / Business Name are inherited
// automatically from their saved role_profile.
const EMPTY = {
  event_name: "",
  about_event: "",
  event_date: "",
  event_time: "",
  end_date: "",
  end_time: "",
  event_photo: "",
  location: "",
  contact_number: "",
  email: "",
  social_links: "",
  website: "",
};

export default function EventCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slotGate, setSlotGate] = useState(null); // { message, active_count, base_allowed, extra_slots, extra_price_cents }
  const [buyingSlot, setBuyingSlot] = useState(false);
  const photoRef = useRef(null);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/event-creator/upload?kind=event_photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, event_photo: data.path }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.event_name.trim()) return toast.error("Event name is required");
    if (!form.end_date || !form.end_time) return toast.error("End date and end time are required");
    if (form.event_date && `${form.end_date}T${form.end_time}` <= `${form.event_date}T${form.event_time || "00:00"}`) {
      return toast.error("The event must end after it starts");
    }
    setSaving(true);
    try {
      await api.post("/event-creator/events", form);
      setSubmitted(true);
    } catch (e) {
      // The 3-event cap returns 402 with a structured payload — surface the
      // paywall dialog instead of a plain toast so the creator can pay.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail && typeof detail === "object" && detail.code === "extra_slot_required") {
        setSlotGate(detail);
      } else {
        toast.error(errMsg(e, "Could not submit event"));
      }
    } finally {
      setSaving(false);
    }
  };

  const payForExtraSlot = async () => {
    setBuyingSlot(true);
    try {
      // Send the event payload with the init call so the backend can store
      // it on the subscription doc. After Paystack confirms the charge the
      // backend auto-creates the event with status=pending — no manual
      // resubmit from the dashboard required.
      const { data } = await api.post("/event-creator/extra-slot/initialize", { event: form });
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error("Could not start checkout for the extra event slot");
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
    } finally {
      setBuyingSlot(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="event-create">
      <Dialog open={!!slotGate} onOpenChange={(o) => { if (!o) setSlotGate(null); }}>
        <DialogContent className="max-w-md" data-testid="extra-slot-dialog">
          <DialogHeader>
            <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-terracotta-light items-center justify-center">
              <Lock className="w-7 h-7 text-terracotta-dark" />
            </div>
            <DialogTitle className="text-center font-heading font-black text-forest text-xl">
              Extra event slot needed
            </DialogTitle>
            <DialogDescription className="text-center text-forest/70 pt-2 leading-relaxed" data-testid="extra-slot-msg">
              {slotGate?.message || "You've reached the 3-event limit on your Event Creator subscription. Additional events are R299 per month per extra event. Please add an extra event slot before publishing this event."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-2xl bg-softstone/70 border border-sage/20 p-3 text-xs text-forest/70 space-y-1" data-testid="extra-slot-summary">
            <p><strong>Active events:</strong> {slotGate?.active_count ?? "—"} of {(slotGate?.base_allowed ?? 3) + (slotGate?.extra_slots ?? 0)}</p>
            <p><strong>Base cap:</strong> {slotGate?.base_allowed ?? 3} events</p>
            <p><strong>Extra slots you own:</strong> {slotGate?.extra_slots ?? 0}</p>
            <p><strong>Add-on price:</strong> R{((slotGate?.extra_price_cents ?? 29900) / 100).toFixed(0)} / month per extra event</p>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setSlotGate(null)} data-testid="extra-slot-cancel-btn">
              Not now
            </Button>
            <Button
              className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white"
              onClick={payForExtraSlot}
              disabled={buyingSlot}
              data-testid="extra-slot-pay-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {buyingSlot ? "Redirecting..." : `Pay R${((slotGate?.extra_price_cents ?? 29900) / 100).toFixed(0)}/month`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={submitted} onOpenChange={(o) => { if (!o) navigate("/event-creator-portal", { replace: true }); }}>
        <DialogContent className="max-w-md" data-testid="event-create-submitted-dialog">
          <DialogHeader>
            <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-sage-light items-center justify-center">
              <Mail className="w-7 h-7 text-sage-dark" />
            </div>
            <DialogTitle className="text-center font-heading font-black text-forest text-xl">Event submitted</DialogTitle>
            <DialogDescription className="text-center text-forest/70 pt-2" data-testid="event-create-submitted-msg">
              Your event has been submitted for approval. Please check your email for further updates.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => navigate("/event-creator-portal", { replace: true })} data-testid="event-create-ok-btn">
              Back to dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><CalendarPlus className="w-5 h-5 text-white" /></span>
        <div>
          <h1 className="text-2xl font-black text-forest">Create a new event</h1>
          <p className="text-sm text-forest/60">Your logo and organiser name are pulled from your profile automatically.</p>
        </div>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-5">
        <div>
          <Label>Event Name*</Label>
          <Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
            data-testid="new-event-name" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. Autumn Nature Walk" />
        </div>

        <div>
          <Label>About Event</Label>
          <Textarea value={form.about_event} onChange={(e) => setForm({ ...form, about_event: e.target.value })}
            data-testid="new-event-about" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" rows={4}
            placeholder="What families can expect, age ranges, what to bring…" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Event Date</Label>
            <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              data-testid="new-event-date" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
          </div>
          <div>
            <Label>Event Time</Label>
            <Input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}
              data-testid="new-event-time" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
          </div>
          <div>
            <Label>End Date *</Label>
            <Input type="date" required value={form.end_date} min={form.event_date || undefined}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              data-testid="new-event-end-date" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
          </div>
          <div>
            <Label>End Time *</Label>
            <Input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              data-testid="new-event-end-time" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
          </div>
        </div>

        <div>
          <Label>Event Photo / Add</Label>
          <div className="mt-2 flex items-center gap-4">
            {form.event_photo ? (
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                <img src={fileUrl(form.event_photo)} alt="Event photo" className="w-full h-full object-cover" data-testid="new-event-photo-preview" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, event_photo: "" }))}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove photo" data-testid="new-event-photo-remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                <Upload className="w-6 h-6" />
              </div>
            )}
            <div className="flex-1">
              <input ref={photoRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="new-event-photo-input" />
              <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
                onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="new-event-photo-btn">
                <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : (form.event_photo ? "Replace image" : "Upload image")}
              </Button>
              <p className="text-xs text-forest/50 mt-1">This becomes the main image on the event card.</p>
            </div>
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-1"><MapPin className="w-4 h-4 text-sage-dark" /> Location of Event</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="new-event-location" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
            placeholder="e.g. Kirstenbosch Gardens, Cape Town" />
          <p className="text-xs text-forest/50 mt-1">We geocode this on save so families see the event on the map.</p>
        </div>

        <div>
          <Label>Contact Number</Label>
          <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
            data-testid="new-event-contact-number" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. +27 82 000 0000" />
        </div>

        <div>
          <Label>Email Address</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="new-event-email" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="you@yourdomain.co.za" />
        </div>

        <div>
          <Label>Social Links</Label>
          <Input value={form.social_links} onChange={(e) => setForm({ ...form, social_links: e.target.value })}
            data-testid="new-event-social-links" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
            placeholder="Instagram, Facebook or profile URL" />
        </div>

        <div>
          <Label>Website</Label>
          <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            data-testid="new-event-website" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="https://yourbusiness.co.za" />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="rounded-full flex-1" onClick={() => navigate("/event-creator-portal")} data-testid="new-event-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-sage hover:bg-sage-dark text-white flex-1" data-testid="new-event-save-btn">
            {saving ? "Submitting..." : "Save & submit for approval"}
          </Button>
        </div>
      </div>
    </div>
  );
}
